import { Icons } from "assets/icons";
import FilterBar from "components/common/FilterBar";
import NoDataFound from "components/common/NoDataFound";
import { StatusBadge } from "components/common/StatusBadge";
import Switch from "components/common/Switch";
import TableShimmer from "components/common/TableShimmer";
import { handleStatusUpdate } from "helpers/apiUtils";
import React, { useEffect, useState, useCallback, useMemo } from "react";
import { Modal, Table } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { getPlansList } from "store/slices/membershipSlice";
import { showDeleteModal } from "store/slices/modalSlice";
import { ORDER_BY_OPTIONS, STATUS_OPTIONS } from "utils/constants";
import { getActionFlags } from "utils/getActionFlags";
import { formatUTCDate, getMinMaxDate } from "utils/utils";
import Paginations from "components/common/Paginations";
import { usePermission } from "hooks/usePermission";
import PageHeader from "components/common/PageHeader";
import {
  getDynamicPrices,
  resetFilters,
  setFilter,
  updateDynamicPriceStatus,
} from "store/slices/dynamicPricesSlice";
import { formatOptionsForSelect } from "utils/formatOptions";
import DynamicPrices from ".";

const DynamicPricesList = () => {
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const { trash, edit, noPlans, view } = Icons || {};
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { canAction } = usePermission();
  const { canView, canEdit, canDelete, canAdd } = canAction("membership_plan");
  const [dynamicPriceModal, setDynamicPriceModal] = useState(false);

  // Split selectors to prevent unnecessary re-renders
  const {
    loading,
    isLoading,
    items: dynamicPrices,
    loadingItemId,
    totalPages,
    currentPage,
    totalCount,
    filters: { perPage, pageNo, status, orderBy, keyword, dates, planId },
  } = useSelector((state) => state.DynamicPrices);
  const { plansList } = useSelector((state) => state.Membership);

  useEffect(() => {
    if (canView) {
      dispatch(getPlansList());
      getAllData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canView]);

  const getAllData = useCallback(
    (page, action) => {
      const { isReset, isResetKeyword } = getActionFlags(action);

      const payload = {
        perPage,
        pageNo: page || pageNo,
        status: isReset ? -1 : status,
        orderBy: isReset ? -1 : orderBy,
        planId: isReset ? "" : planId,
        keyword: isResetKeyword ? "" : keyword || "",
        dateRange: isReset ? {} : getMinMaxDate(dates),
      };

      dispatch(getDynamicPrices(payload));
    },
    [perPage, pageNo, status, orderBy, keyword, dates, planId, dispatch],
  );

  const handleReset = useCallback(() => {
    dispatch(resetFilters());
    getAllData(1, { actionType: "RESET" });
  }, [dispatch, getAllData]);
  const handleDynamicPriceModalClose = useCallback(() => {
    setDynamicPriceModal(false);
  }, []);

  const handleDynamicPriceModalSuccess = useCallback(() => {
    getAllData();
    setDynamicPriceModal(false);
  }, []);

  const filtersList = useMemo(
    () => [
      {
        key: "status",
        placeholder: t("Status"),
        type: "singleSelect",
        options: STATUS_OPTIONS,
      },
      {
        key: "planId",
        placeholder: t("Select Plan"),
        type: "singleSelect",
        options: formatOptionsForSelect(plansList, language, "title"),
      },
      {
        key: "orderBy",
        placeholder: t("Order By"),
        type: "singleSelect",
        options: ORDER_BY_OPTIONS,
      },
    ],
    [t],
  );

  if (!canView) return null;

  return (
    <div className="page-content addons_page">
      <div className="g_between">
        <PageHeader title={t("Dynamic Prices Rules")} />
      </div>

      <FilterBar
        keyword={keyword}
        setKeyword={(value) => dispatch(setFilter({ key: "keyword", value }))}
        keywordPlaceholder={t("Search by max customer count")}
        filters={filtersList}
        selectedFilters={{ keyword, status, orderBy, planId }}
        onFilterUpdate={(key, value) => dispatch(setFilter({ key, value }))}
        dates={dates}
        setDates={(value) => dispatch(setFilter({ key: "dates", value }))}
        onSearch={getAllData}
        onReset={handleReset}
        isBtn={true}
        btnText={t("Add Prices")}
        onBtnClick={() => navigate("/dynamic-prices/add")}
      />

      {loading && <TableShimmer rows={12} columns={8} />}

      {!loading && dynamicPrices?.length === 0 && (
        <NoDataFound title={t("No dynamic prices found")} icon={noPlans} />
      )}

      {!loading && dynamicPrices?.length > 0 && (
        <div className="common_table_style">
          <Table hover responsive>
            <thead className="t_head">
              <tr>
                <th style={{ width: 70 }}>{t("S.No")}</th>
                <th>{t("Plan Type")}</th>
                <th>{t("Created Date")}</th>
                <th>{t("Status")}</th>
                <th>{t("Actions")}</th>
              </tr>
            </thead>
            <tbody>
              {dynamicPrices?.length > 0 ? (
                dynamicPrices.map((addon, index) => {
                  const { _id, planId, status, createdAt } = addon || {};
                  return (
                    <tr key={_id}>
                      <td>{index + 1}</td>

                      <td>
                        <div className="">
                          <Link
                            to={`/membership/plans/plan-details?id=${planId?._id}`}
                          >
                            {planId?.planType}
                          </Link>
                        </div>
                      </td>
                      <td>
                        <div className="ltr_text">
                          {formatUTCDate(createdAt)}
                        </div>
                      </td>
                      <td>
                        <StatusBadge status={status} t={t} />
                      </td>
                      <td>
                        <div className="actions">
                          {canEdit && (
                            <>
                              <Switch
                                checked={status === 1}
                                handleOnChange={() => {
                                  handleStatusUpdate({
                                    _id,
                                    status,
                                    dispatch,
                                    action: updateDynamicPriceStatus,
                                  });
                                }}
                                disabled={isLoading && loadingItemId === _id}
                              />
                              <img
                                src={edit}
                                width={20}
                                height={20}
                                alt="edit"
                                onClick={() =>
                                  navigate(`/dynamic-prices/edit?id=${_id}`)
                                }
                              />
                            </>
                          )}

                          {canDelete && (
                            <img
                              src={trash}
                              width={20}
                              height={20}
                              alt="delete"
                              onClick={() =>
                                dispatch(
                                  showDeleteModal({
                                    title: t("Delete Plan"),
                                    message: t(
                                      "Are you sure you want to delete this Price?",
                                    ),
                                    itemId: _id,
                                    actionType: "DYNAMIC_PRICES",
                                  }),
                                )
                              }
                            />
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={7} className="text-center py-3 text-muted">
                    {t("No Dynamic Prices found")}
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </div>
      )}

      {!loading && (
        <Paginations
          currentPage={currentPage}
          totalPages={totalPages}
          totalCount={totalCount}
          onPageChange={getAllData}
        />
      )}
      {dynamicPriceModal && (
        <Modal
          show={dynamicPriceModal}
          onHide={handleDynamicPriceModalClose}
          centered
          className="common_modal w_876"
        >
          <h2 className="modal_title">{t("Manage Dynamic Prices")}</h2>
          <div className="content">
            <DynamicPrices
              onCancel={handleDynamicPriceModalClose}
              onSuccess={handleDynamicPriceModalSuccess}
            />
          </div>
        </Modal>
      )}
    </div>
  );
};

export default DynamicPricesList;
